import kfuseRumSDK from 'kf-browser-sdk';

// // Initialize Kfuse browser agent

export const setKfuseMetrics = (metrics: Record<string, any>): void => {
  try {
    kfuseRumSDK.setUser(metrics);
  } catch (error) {
    console.error('Failed to set Kfuse metrics:', error);
  }
};

export const setNoticeError = (
  error: Error | string,
  attributes?: Record<string, any>
): void => {
  try {
    kfuseRumSDK.addError(error, attributes || {});
  } catch (e) {
    console.error('Failed to log error:', e);
  }
};

export const trackEvent = (
  eventName: string,
  attributes?: Record<string, any>
): void => {
  try {
    kfuseRumSDK.addAction(eventName, attributes || {});
  } catch (error) {
    console.error(`Failed to track event '${eventName}':`, error);
  }
};

export const trackApiFailure = (details: {
  endpoint: string;
  method?: string;
  statusCode: number;
  errorMessage?: string;
  errorResponse?: Record<string, any>; // Full API error response
  requestDetails?: Record<string, any>; // Request details for debugging
  traceId?: string;
  duration?: number;
  context?: {
    page?: string;
    component?: string;
    flowStep?: string;
  };
}): void => {
  const {
    endpoint,
    method = 'UNKNOWN',
    statusCode,
    errorMessage,
    errorResponse,
    requestDetails,
    traceId,
    duration,
    context,
  } = details;

  let errorTitle = `API Error ${statusCode}: ${method} ${endpoint}`;
  if (context?.component && context?.flowStep) {
    errorTitle = `API Error ${statusCode}: ${context.component} - ${context.flowStep}`;
  }

  const attributes = {
    errorType: 'api_error',
    errorCategory: 'api',
    endpoint,
    method,
    statusCode,
    errorMessage: errorMessage || 'Unknown error',
    apiResponseMessage: errorMessage,
    errorResponse: errorResponse ? JSON.stringify(errorResponse) : undefined,
    timestamp: new Date().toISOString(),
    page: context?.page || window.location.pathname,
    component: context?.component,
    flowStep: context?.flowStep,
    ...(duration !== undefined ? { duration } : {}),
    ...(traceId !== undefined ? { traceId } : {}),
  };

  // Log to console for debugging (includes traceId for correlation with backend logs)
  console.error(`[API Failure] ${errorTitle}`, {
    traceId,
    duration: duration ? `${duration}ms` : undefined,
    request: requestDetails,
    response: {
      statusCode,
      errorResponse,
    },
  });

  trackEvent('api_failure', attributes);

  const apiError = new Error(errorTitle);
  (apiError as any).name = context?.component
    ? `${context.component}Error`
    : 'APIError';
  setNoticeError(apiError, attributes);
};

export const getBrowserAgent = () => {
  return kfuseRumSDK;
};

// Global error handlers
export const setupGlobalErrorHandlers = (): (() => void) => {
  const originalOnError = window.onerror;

  window.onerror = (message, source, lineno, colno, error) => {
    setNoticeError(error || String(message), { source, lineno, colno });
    return typeof originalOnError === 'function'
      ? originalOnError(message, source, lineno, colno, error)
      : false;
  };

  const handleRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    setNoticeError(
      reason instanceof Error ? reason : new Error(String(reason))
    );
  };

  window.addEventListener('unhandledrejection', handleRejection);

  return () => {
    window.onerror = originalOnError;
    window.removeEventListener('unhandledrejection', handleRejection);
  };
};
